/*
 * Signup directly (no existing Microsoft account)
 */
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { Iuser } from '../../models';
import { signUpDirect } from '../../actions/userActions';
import { removeLoginRedirect } from '../../actions/redirectToReferrerAction';
import { Button } from 'react-bootstrap';
import { RouteComponentProps } from 'react-router-dom';
import { withTranslation, WithTranslation } from 'react-i18next';
import UserForm from './UserForm';
import { IregisterUserFormValues } from '../../modelsForms';
import { selectIsLoading } from '../../reducers/commonReducers';
import { IinitialState } from '../../reducers';

interface Iprops extends RouteComponentProps<{}> {
    removeLoginRedirect: typeof removeLoginRedirect;
    user: Iuser;
    signUpDirect: (tempUser: IregisterUserFormValues) => Promise<void>;
    loading: boolean;
}

const SignUpSuccess = (props: any) => {
    const { t } = props;
    return (
        <div className="login-form signup-success" style={{ color: 'white' }}>
            <h2>{t('accountPending')}</h2>
            <p>{t('successBody1')}</p>
            <p>
                {t('successBody2')} <br /> {t('successBody3')}
            </p>
            <Button
                bsStyle="link"
                className="ok-button"
                style={{ color: 'white', margin: '12px' }}
                onClick={props.handleCancel}
            >
                {t('ok')}
            </Button>
        </div>
    );
};

const SignUpDirect: React.FC<Iprops & WithTranslation> = props => {
    const [redirectToLogin, setRedirectToLogin] = useState<boolean>(false);
    const [showSignupSuccess, setShowSignupSuccess] = useState<boolean>(false);
    const { t } = props;

    const cancel = () => {
        setRedirectToLogin(true);
    };

    const handleSubmit = async (newUser: IregisterUserFormValues) => {
        try {
            await props.signUpDirect(newUser);
            setShowSignupSuccess(true);
        } catch (error) {
            console.error(error);
        }
    };

    // dev-only: signupTestMode lets a signed-in dev exercise this page as if
    // they were a brand-new user (see msalFetch's X-Signup-Test header);
    // stripped from production builds by the NODE_ENV check
    const signupTestMode =
        process.env.NODE_ENV === 'development' &&
        localStorage.getItem('signupTestMode') === '1';

    if (props.user.id.length && !signupTestMode) {
        props.removeLoginRedirect();
        return <Redirect to={'/dashboard'} />;
    }
    if (redirectToLogin) {
        return <Redirect to={'/'} />;
    }
    return (
        <div className="signup">
            <img
                src={require('../../images/beaconHome-768w.jpg')}
                srcSet={`
                    ${require('../../images/beaconHome-320w.jpg')} 320w,
                    ${require('../../images/beaconHome-768w.jpg')} 768w,
                `}
                sizes="(max-width: 768px) 100vw, 50vw"
                alt="BeaconMedaes"
                className="beacon-home-xs"
            />
            <img
                src={require('../../images/beaconHome-1920w.jpg')}
                srcSet={`
                    ${require('../../images/beaconHome-1024w.jpg')} 1024w,
                    ${require('../../images/beaconHome-1920w.jpg')} 1920w,
                    ${require('../../images/beaconHome-2560w.jpg')} 2560w
                `}
                sizes="(min-width: 768px) 50vw, 100vw"
                alt="BeaconMedaes"
                className="beacon-home-lg"
            />
            <div className="wrapper">
                {showSignupSuccess ? (
                    <SignUpSuccess handleCancel={cancel} t={t} />
                ) : (
                    <div className="signup-form-panel">
                        <div className="beacon-form-info">
                            <h1 className="welcome-text">
                                Welcome to MyMedGas
                            </h1>
                            <p>
                                Thank you for registering with MyMedGas, a
                                platform developed by BeaconMedaes, a part of
                                the Atlas Copco Group. Your registered email and
                                password provide access to multiple online
                                systems, including MyMedGas, MyMedGas
                                Credentialing, BeaconMedaes Training, SmartLINK,
                                OnePortal, and more.
                            </p>

                            <p>
                                As MyMedGas contains confidential information
                                related to healthcare facilities and hospitals,
                                we require additional details about your
                                facility and role before granting access. Our
                                back-office team will review your submission to
                                ensure the appropriate access level is assigned.
                                Once you complete your sign-up, your account
                                will be placed under verification and
                                activation, which typically takes one to two
                                business days. If you have any questions, please
                                contact us at{' '}
                                <a href="mailto:mymedgas.support@beaconmedaes.com">
                                    mymedgas.support@beaconmedaes.com
                                </a>
                            </p>
                            <p>
                                If you are only interested in Training and
                                Credentialing, you do not need to register for
                                MyMedGas. Instead, please visit{' '}
                                <a
                                    href="https://www.beaconmedaes.com/en-us/services/training-optimization/training"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    our training page
                                </a>{' '}
                                to access the necessary resources directly.
                            </p>
                        </div>
                        <UserForm
                            handleSubmit={handleSubmit}
                            handleCancel={cancel}
                            loading={props.loading}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

const mapStateToProps = (state: IinitialState, ownProps: any) => {
    return {
        user: state.user,
        loading: selectIsLoading(state)
    };
};

export default withTranslation('auth')(
    connect(mapStateToProps, {
        removeLoginRedirect,
        signUpDirect
    })(SignUpDirect)
);
